import { useRef, useEffect, useState } from "react";

import { ImageType } from "@/utils/shared-atom-types";
import { getImageUrl } from "@/utils/GetImageUrl";
import { Text } from "@/components/atoms/Text";
import { Image } from "@/components/atoms/Image";
import { Button } from "@/components/atoms/Button";
import { useScrollPosition } from "@/utils/hooks/UseScrollPosition";
import { ButtonVariant } from "@/components/atoms/Button/types";
import { useClassNames } from "@/utils/classNames";

import { SpotlightProps } from "./types";
import styles from "./Spotlight.module.scss";

/**
 * Spotlight component to display a single featured asset with prominent visual treatment.
 * Features a scroll-based scale animation that grows from 0.8 to 1.0 as the element enters the viewport.
 *
 * @param asset - The asset to feature in the spotlight
 * @param title - Optional title override (defaults to asset title)
 * @param copy - Optional copy or description text
 * @param scrollContainerRef - Optional ref to scroll container for animations (defaults to window)
 * @param className - Additional CSS class for styling
 * @param attrs - Additional HTML attributes for the div element
 * @returns A spotlight component featuring a single asset
 *
 * @example
 * ```tsx
 * // Basic usage with window scrolling
 * <Spotlight asset={assetResponse} />
 *
 * // With custom scroll container
 * const containerRef = useRef<HTMLDivElement>(null);
 * <Spotlight asset={assetResponse} scrollContainerRef={containerRef} />
 * ```
 */
const Spotlight = ({
  asset,
  title,
  copy,
  className,
  scrollContainerRef,
  ...attrs
}: SpotlightProps) => {
  const spotlightRef = useRef<HTMLDivElement>(null);
  const scrollY = useScrollPosition(scrollContainerRef);
  const [scale, setScale] = useState(0.8);

  // Calculate scale based on scroll position
  useEffect(() => {
    if (!spotlightRef.current) return;

    const scrollContainer = scrollContainerRef?.current;
    const rect = spotlightRef.current.getBoundingClientRect();

    // Use container dimensions if available, otherwise use window
    const containerRect = scrollContainer?.getBoundingClientRect();
    const viewportHeight = containerRect?.height || window.innerHeight;
    const viewportTop = containerRect?.top || 0;

    // Calculate element position relative to viewport
    const elementTop = rect.top - viewportTop;
    const elementHeight = rect.height;
    const elementBottom = elementTop + elementHeight;

    /** Element is in view when its top is between 0 and viewportHeight */
    if (elementTop < viewportHeight && elementBottom > 0) {
      /** Calculate progress: 0 when element starts entering from bottom, 1 when fully in view */
      /** Element is fully in view when bottom edge is above viewportHeight and top edge is above 0 */
      if (elementBottom <= viewportHeight && elementTop >= 0) {
        /** Fully in view - scale at 100% */
        setScale(1.0);
      } else if (elementTop < viewportHeight && elementTop > 0) {
        /** Element is entering from bottom */
        const visibleHeight = Math.min(
          elementHeight,
          viewportHeight - elementTop,
        );
        const progress = visibleHeight / elementHeight;
        /** Scale from 0.8 to 1.0 as element enters */
        const newScale = 0.8 + progress * 0.2;
        setScale(newScale);
      } else {
        /** Element is exiting from top or fully visible */
        setScale(1.0);
      }
    }
  }, [scrollY, scrollContainerRef]);

  /** Get the appropriate background image - use Scene34 for Spotlight */
  const backgroundImage = getImageUrl(asset, ImageType.Scene34, {
    width: 750,
    height: 1000,
    quality: 7,
  });

  /** Get the title logo if available */
  const logoImage = getImageUrl(asset, ImageType.TitleLogo, {
    width: 500,
    height: 200,
    quality: 7,
  });

  /** Use provided title or fall back to asset title */
  const displayTitle = title || asset.attributes?.title;

  /** Use provided tagline or fall back to asset tagline/description */
  const copyText = copy
    ? copy
    : ("tagline" in (asset.attributes || {})
        ? (asset.attributes as { tagline?: string }).tagline
        : undefined) ||
      ("shortDescription" in (asset.attributes || {})
        ? (asset.attributes as { shortDescription?: string }).shortDescription
        : undefined) ||
      ("description" in (asset.attributes || {})
        ? (asset.attributes as { description?: string }).description
        : undefined);

  const cn = useClassNames(styles, styles["spotlight"], className);

  return (
    <div
      ref={spotlightRef}
      className={cn}
      style={{ transform: `scale(${scale})` }}
      {...attrs}
    >
      {backgroundImage && (
        <Image
          src={backgroundImage}
          alt={displayTitle}
          className={styles["spotlight-background"]}
        />
      )}

      <div className={styles["spotlight-content"]}>
        {logoImage ? (
          <Image
            src={logoImage}
            className={styles["spotlight-logo"]}
            alt={displayTitle}
          />
        ) : (
          displayTitle && (
            <Text variant="h2" className={styles["spotlight-title"]}>
              {displayTitle}
            </Text>
          )
        )}

        {copyText && (
          <Text variant="p" className={styles["spotlight-copy"]}>
            {copyText}
          </Text>
        )}

        <Button
          className={styles["spotlight-button"]}
          variant={ButtonVariant.Secondary}
        >
          Explore Collection
        </Button>
      </div>
    </div>
  );
};

export { Spotlight };
